/**
 * The aim, as a field rather than as an outline.
 *
 * An armed ability has to say WHERE it lands without drawing a diagram: a lit
 * polygon per cell reads as a spreadsheet selection, and the squares survive every
 * attempt to soften them because the shape itself is square. So the footprint is
 * built the way weather is drawn — a continuous scalar field, sampled at intervals
 * as contours — and the cells only ever act as its sources.
 *
 * THE CONCENTRIC RINGS ARE GONE, and their removal is the point of this file's
 * present shape. A contour field is the right way to draw a scalar quantity and the
 * wrong way to draw an aim: bands read off a radius are radially symmetric, so they
 * say the same thing about a 5x5 blast, a cone and a single cell — a bullseye
 * centred on the pointer, laid over the art, telling the player nothing about which
 * ground the shot actually claims. What is left is the thing the rings were sitting
 * on top of: ONE gradient in the skill's own material, cut to the cells it will hit.
 *
 * Three pieces, composited each frame:
 *
 *   BODY   a gaussian per affected cell, summed. This is what makes a cell the SIZE
 *          of the impact rather than its boundary; neighbours fuse without a seam
 *          and a lone cell reads as a disc. Keyed on the cell set with colour and
 *          validity, so it is rasterised once per shape and reused across every pan,
 *          zoom and pointer move. THIS is what says "exactly here".
 *   FLOW   the material, spread over distance from the aim point: a bleached centre
 *          cooling out through the violation's own colour to where it dies. Radially
 *          symmetric, therefore independent of WHERE the aim is — the texture is
 *          translated under the pointer rather than rebuilt for each position.
 *   MASK   the flow is multiplied INTO the body with `destination-in`, so the light
 *          is shaped by the cells while its brightest point is free to sit wherever
 *          the pointer is.
 *
 * What moves is not the gradient but its surface: the material breathes in patches,
 * each patch on its own phase taken from a noise field, so the tint drifts the way
 * weather drifts rather than pulsing as a whole. One cycle of the phase returns the
 * image exactly, so a fixed set of frames covering it loops seamlessly and is
 * rasterised once. Nothing here may run faster than AIM_FLOW_PERIOD — this is a
 * field settling, not a pulse, and anything brisk turns it back into a blinking HUD.
 *
 * Vue-agnostic and board-agnostic on purpose. The player's tactical map and the duel
 * bench both draw through this one instance-per-board, so the bench's aim IS the
 * game's aim; the caller supplies only its own 2D context, its own cells, and one
 * projector callback. The caches are instance state rather than module singletons —
 * two boards on screen at once must not evict each other's frames.
 */

/** Out of range or otherwise refused. Never used for a shot that would land. */
export const AIM_DENIED_COLOR = { r: 130, g: 140, b: 155 };

/**
 * How far the tint carries from the aim point, in cells, as the scale of a
 * `1/(1+q^2)` falloff: half strength at this distance, a fifth at twice it.
 *
 * Wide on purpose. The old core scale was 0.4 of a cell — right for lighting the
 * pointer, useless for describing an area, since every cell of a 5x5 blast beyond
 * the middle one sat at under a tenth strength and the shape was carried entirely
 * by the rings. At 1.6 the gradient still says clearly where the shot is centred
 * while the far corners of a wide spray stay legibly lit.
 */
const AIM_SPREAD_SCALE = 1.6;

/**
 * How much of the tint the drifting patches may take away.
 *
 * This is the whole of the aim's motion, so it has to be enough to see and little
 * enough to ignore. At 0.3 the surface reads as something moving over the ground;
 * past about 0.45 the patches start reading as holes in the footprint, which
 * contradicts the one thing the field exists to state.
 */
const AIM_MOTTLE_DEPTH = 0.3;

/**
 * Radians of phase per cell of warp displacement. The warp field is normalised to a
 * 0.11-cell standard deviation, so this puts the typical patch about 3.7 radians off
 * its neighbour's phase — patches that clearly breathe independently, over features
 * the warp's own scale makes about 1.7 cells across.
 */
const AIM_MOTTLE_TURNS = 34;

/**
 * Radius of the gaussian each affected cell emits, in cells.
 *
 * Tightened from 0.62 once the rings went. With the contours carrying the shape, the
 * body only had to be a soft glow underneath them and could afford to spill; it is
 * now the ONLY statement of which ground is claimed, so it has to hold the cell
 * union close. At 0.54 a lone cell still reads as a disc rather than a square and
 * neighbours still fuse without a seam, but the light is gone within about two
 * thirds of a cell of the boundary instead of drifting a cell and a half past it.
 */
const AIM_FIELD_SIGMA = 0.54;

/** How many rasterised bodies to keep. Enough to cover the cells meeting at a
 *  corner and the shapes either side of a rotation, so hovering a boundary reuses
 *  them instead of rebuilding on every flip. */
export const AIM_BODY_CACHE_SIZE = 8;

/** How many flow frame sets to keep. Two covers an ability's own colour either
 *  side of its maximum range, which is the crossing that used to rebuild them. */
export const AIM_FLOW_CACHE_SIZE = 3;

/**
 * How far the radial flow is rasterised, in cells. It only has to outreach the
 * widest spray anyone aims; past its rim the texture eases to nothing so its own
 * edge can never show, and it is rasterised once per instance.
 */
const AIM_FLOW_REACH = 4.6;

/** Texture pixels per cell for the flow. Nothing in it is finer than the warp's own
 *  1.7-cell features now that the bands are gone, so this is about resampling the
 *  gradient smoothly rather than about resolving anything narrow. */
const AIM_FLOW_DENSITY = 30;

/**
 * Frames covering one full cycle of the rings' outward travel.
 *
 * Two neighbouring frames are blended to land between them. The mottle is a plain
 * cosine in phase now rather than a fifth power of one, so a coarse set would blend
 * cleanly - but the count is kept at 32 because the frames are also what the priming
 * budget is spread over, and a set built in eight chunks lands sooner than one built
 * in two.
 */
export const AIM_FLOW_FRAMES = 32;

/** Seconds for the material to breathe once. */
export const AIM_FLOW_PERIOD = 2.6;

/**
 * Typical displacement the noise gives the rings, in cells - a standard deviation,
 * not a bound, and the warp field is normalised to hit it exactly. Stating it as an
 * amplitude was the bug: three octaves of noise summed in [0,1] cluster hard around
 * their mean rather than spanning it, so mapping the sum onto +/-1 landed at about a
 * third of the intended strength and the rings came out nearly true.
 *
 * Against a 0.32-cell band spacing this puts the typical pull at about a third of a
 * spacing and the extremes near a whole one - clearly morphed, while the warp's
 * gradient stays well under 1 so neighbouring rings undulate without crossing.
 */
const AIM_FLOW_WARP = 0.11;

/** Noise frequency. Lower is broader: this gives features about 1.7 cells across,
 *  so the rings sweep rather than fray. */
const AIM_FLOW_WARP_SCALE = 0.6;

/**
 * Value noise, and three octaves of it. Deterministic from position alone, so the
 * warp it produces is a fixed property of the field rather than something that
 * shimmers between frames.
 */
function noiseHash(x, y) {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
  return n - Math.floor(n);
}

function valueNoise(x, y) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = noiseHash(xi, yi);
  const b = noiseHash(xi + 1, yi);
  const c = noiseHash(xi, yi + 1);
  const d = noiseHash(xi + 1, yi + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

/** Signed, so octaves cancel around zero instead of piling up around a half. */
export function fractalNoise(x, y) {
  return (valueNoise(x, y) * 2 - 1) * 0.6
    + (valueNoise(x * 2.1 + 5.2, y * 2.1 + 1.3) * 2 - 1) * 0.3
    + (valueNoise(x * 4.3 + 9.1, y * 4.3 + 7.7) * 2 - 1) * 0.1;
}

/** `#rrggbb` or `#rgb` to the {r,g,b} the field wants. Boards that keep their aim
 *  palette as CSS strings adapt here rather than each keeping their own parser. */
export function rgbFromHex(hex, fallback = AIM_DENIED_COLOR) {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!match) return fallback;
  const digits = match[1].length === 3
    ? match[1].split('').map(character => character + character).join('')
    : match[1];
  return {
    r: parseInt(digits.slice(0, 2), 16),
    g: parseInt(digits.slice(2, 4), 16),
    b: parseInt(digits.slice(4, 6), 16),
  };
}

/**
 * A palette, from whatever the caller had.
 *
 * Boards that already know the skill's material hand over three stops from
 * `aimMaterial.aimPalette`. Boards and benches that only have a flat swatch hand
 * over one, and the other two are derived from it — bleached toward white for the
 * emitting centre, sunk toward black for where it cools — so a caller with no
 * violation to hand still gets a ramp rather than a single mid-tone, which on a
 * near-black board is the difference between light and paint.
 */
export function normalizePalette(value) {
  if (!value) return normalizePalette(AIM_DENIED_COLOR);
  if (value.color && value.core && value.deep) return value;
  const color = { r: value.r | 0, g: value.g | 0, b: value.b | 0 };
  return {
    color,
    core: mixRgb(color, { r: 255, g: 255, b: 255 }, 0.72),
    deep: mixRgb(color, { r: 0, g: 0, b: 0 }, 0.72),
  };
}

/**
 * Keyed on the middle stop alone. The other two are a deterministic function of the
 * violation and intent that produced it, so two palettes agreeing here agree
 * everywhere; and a flat swatch keys as its own `r,g,b`, which is what the caches
 * were keyed on before there were three stops.
 */
export function paletteKey(palette) {
  return `${palette.color.r},${palette.color.g},${palette.color.b}`;
}

function mixRgb(a, b, amount) {
  return {
    r: Math.round(a.r + (b.r - a.r) * amount),
    g: Math.round(a.g + (b.g - a.g) * amount),
    b: Math.round(a.b + (b.b - a.b) * amount),
  };
}

/** deep -> color -> core, over [0,1]. The ramp is what makes the tint read as a
 *  material lit from inside rather than as a wash of one colour at varying alpha. */
export function rampAt(palette, t) {
  const level = t < 0 ? 0 : (t > 1 ? 1 : t);
  return level < 0.5
    ? mixRgb(palette.deep, palette.color, level * 2)
    : mixRgb(palette.color, palette.core, (level - 0.5) * 2);
}

export class AimField {
  /**
   * @param options  bodyCacheSize / flowCacheSize  eviction limits, per instance
   */
  constructor({ bodyCacheSize = AIM_BODY_CACHE_SIZE, flowCacheSize = AIM_FLOW_CACHE_SIZE } = {}) {
    this.bodyCacheSize = bodyCacheSize;
    this.flowCacheSize = flowCacheSize;

    this.bodyCache = new Map(); // Rasterised impact bodies, keyed by the set of affected cells
    this.latticeCache = null; // Radius and warp lookups, shared by every frame set
    this.flowCache = new Map(); // Flow frame sets, keyed by colour and range state
    this.composite = null; // Scratch canvas the two are combined in each frame
    this.disposed = false;
  }

  /**
   * The gaussian sum, rasterised.
   *
   * Several shapes are kept, not one. Along a cell boundary - and worse at a corner,
   * where four meet - the resolving cell flips back and forth under the smallest
   * movement, and with a single slot every flip missed and re-rasterised the whole
   * body. That stall, several times a second, was the blinking.
   *
   * @param cells  [{ x, y }] in whole grid coordinates
   */
  bodyTexture(cells, color, valid) {
    const signature = [
      valid ? 'v' : 'x',
      `${color.r},${color.g},${color.b}`,
      cells.map(cell => `${cell.x},${cell.y}`).sort().join(' ')
    ].join('|');

    const cached = this.bodyCache.get(signature);
    if (cached) {
      // Refresh its place in the eviction order.
      this.bodyCache.delete(signature);
      this.bodyCache.set(signature, cached);
      return cached;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    cells.forEach(cell => {
      minX = Math.min(minX, cell.x);
      minY = Math.min(minY, cell.y);
      maxX = Math.max(maxX, cell.x);
      maxY = Math.max(maxY, cell.y);
    });

    // Padded until the gaussians have died out, so the halo always ends inside the
    // texture and is never cut off by its edge. At 2.6 sigma what is left outside is
    // under a percent of the peak — below the point where the texture's own edge
    // could show, and a third of a cell tighter to the claim than 3.2 was.
    const pad = AIM_FIELD_SIGMA * 2.6;
    const x0 = minX - pad;
    const y0 = minY - pad;
    const spanX = maxX + 1 + pad - x0;
    const spanY = maxY + 1 + pad - y0;

    const density = 44;
    const width = Math.max(48, Math.min(320, Math.round(spanX * density)));
    const height = Math.max(48, Math.min(320, Math.round(spanY * density)));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    const image = context.createImageData(width, height);
    const pixels = image.data;

    const strideX = spanX / width;
    const strideY = spanY / height;
    const sigmaSq = AIM_FIELD_SIGMA * AIM_FIELD_SIGMA;
    const centres = cells.map(cell => [cell.x + 0.5, cell.y + 0.5]);

    /*
     * The union of discs is round per cell and square in the large: five cells by five
     * is twenty-five round sources arranged in a square, and what the eye reads at
     * arm's length is the square. So the sum is enclosed in one soft disc about the
     * footprint's own centroid, sized to reach its furthest cell and feathered over
     * most of a cell, which takes the corners off the silhouette without touching
     * anything in the middle.
     *
     * It cannot cost the claim a cell. The envelope is full out to the furthest cell
     * centre and only falls beyond it, so every cell the shot hits is inside the part
     * of the envelope that is exactly one.
     */
    const centroid = centres.reduce((sum, c) => [sum[0] + c[0] / centres.length, sum[1] + c[1] / centres.length], [0, 0]);
    const enclosing = centres.reduce(
      (far, c) => Math.max(far, Math.hypot(c[0] - centroid[0], c[1] - centroid[1])),
      0,
    ) + AIM_FIELD_SIGMA;
    const envelopeOuter = enclosing + 0.85;

    for (let row = 0; row < height; row++) {
      const gy = y0 + (row + 0.5) * strideY;
      for (let column = 0; column < width; column++) {
        const gx = x0 + (column + 0.5) * strideX;

        let sum = 0;
        for (let i = 0; i < centres.length; i++) {
          const dx = gx - centres[i][0];
          const dy = gy - centres[i][1];
          const q = (dx * dx + dy * dy) / sigmaSq;
          if (q < 12) sum += Math.exp(-q);
        }
        if (sum < 0.0015) continue;

        const fromCentre = Math.hypot(gx - centroid[0], gy - centroid[1]);
        const envelope = fromCentre <= enclosing
          ? 1
          : Math.max(0, 1 - (fromCentre - enclosing) / (envelopeOuter - enclosing));
        if (envelope <= 0) continue;

        const offset = (row * width + column) * 4;
        pixels[offset] = color.r;
        pixels[offset + 1] = color.g;
        pixels[offset + 2] = color.b;
        /*
         * Saturating, not linear. A covered cell has to be fully claimed and the
         * fall has to happen at its rim — a gentler curve leaves the interior of a
         * wide spray dimmer than its centre, which reads as the middle cells being
         * hit harder than the outer ones. They are not; they are hit the same.
         */
        pixels[offset + 3] = (1 - Math.exp(-sum * 2.6)) * envelope * envelope * 255;
      }
    }

    context.putImageData(image, 0, 0);

    const entry = {
      signature, canvas, x0, y0, spanX, spanY,
      pixelsPerCellX: width / spanX,
      pixelsPerCellY: height / spanY
    };

    this.bodyCache.set(signature, entry);
    while (this.bodyCache.size > this.bodyCacheSize) {
      this.bodyCache.delete(this.bodyCache.keys().next().value);
    }
    return entry;
  }

  /**
   * The radius and warp lookups every frame set reads from. These are the expensive
   * half - the noise alone is over a million sines - and they depend on neither
   * colour nor range, so they are built once per instance and shared.
   */
  lattice() {
    if (this.latticeCache) return this.latticeCache;

    const radiusInCells = AIM_FLOW_REACH;
    const size = Math.round(radiusInCells * 2 * AIM_FLOW_DENSITY);
    const stride = (radiusInCells * 2) / size;

    const radii = new Float32Array(size * size);
    const warps = new Float32Array(size * size);

    for (let row = 0; row < size; row++) {
      const dy = -radiusInCells + (row + 0.5) * stride;
      for (let column = 0; column < size; column++) {
        const dx = -radiusInCells + (column + 0.5) * stride;
        const index = row * size + column;
        radii[index] = Math.hypot(dx, dy);
        warps[index] = fractalNoise(dx * AIM_FLOW_WARP_SCALE, dy * AIM_FLOW_WARP_SCALE);
      }
    }

    // Normalise the warp to the displacement it is supposed to have. Octave weights
    // and the interpolation both change how far the sum actually spreads, so
    // deriving the scale from the field itself is the only way the constant above
    // can mean what it says.
    let total = 0;
    let totalSquared = 0;
    for (let i = 0; i < warps.length; i++) {
      total += warps[i];
      totalSquared += warps[i] * warps[i];
    }
    const mean = total / warps.length;
    const spread = Math.sqrt(Math.max(1e-9, totalSquared / warps.length - mean * mean));
    const gain = AIM_FLOW_WARP / spread;
    for (let i = 0; i < warps.length; i++) {
      warps[i] = (warps[i] - mean) * gain;
    }

    this.latticeCache = { size, radiusInCells, radii, warps };
    return this.latticeCache;
  }

  /**
   * Sets are kept per colour and range state, not one at a time. Crossing the
   * ability's maximum range flips the aim between live and refused, which changes
   * both - and with a single slot that threw away all thirty-two frames on every
   * crossing and built them again. Measured at 8-13 ms a pointer move against 0.7 ms
   * normally: most of a frame's budget, over and over, which is what was being seen
   * as the aim blinking.
   *
   * Frame zero is built immediately and the rest arrive as the cycle first asks for
   * them, so arming an ability never blocks on the whole set.
   */
  flowFrames(color, valid) {
    const palette = normalizePalette(color);
    const signature = `${valid ? 'v' : 'x'}|${paletteKey(palette)}`;

    const cached = this.flowCache.get(signature);
    if (cached) {
      this.flowCache.delete(signature);
      this.flowCache.set(signature, cached);
      return cached;
    }

    const lattice = this.lattice();
    const set = {
      signature,
      color,
      palette,
      valid,
      lattice,
      radiusInCells: lattice.radiusInCells,
      frames: new Array(AIM_FLOW_FRAMES).fill(null)
    };

    this.buildFlowFrame(set, 0);
    this.flowCache.set(signature, set);
    while (this.flowCache.size > this.flowCacheSize) {
      this.flowCache.delete(this.flowCache.keys().next().value);
    }
    return set;
  }

  /**
   * Fills in the rest of a set's cycle off the main flow of work, so no pointer move
   * ever pays for a frame. Deliberately a timer and not an animation frame:
   * requestAnimationFrame is suspended outright in a hidden tab, which would leave a
   * set half built and hand the cost straight back to the first move that needed it.
   * Stops as soon as the set is evicted or the field is disposed.
   */
  prime(set) {
    const fill = () => {
      if (this.disposed) return;
      if (this.flowCache.get(set.signature) !== set) return;
      let budget = 4;
      while (budget-- > 0) {
        const next = set.frames.indexOf(null);
        if (next === -1) return;
        this.buildFlowFrame(set, next);
      }
      setTimeout(fill, 0);
    };
    setTimeout(fill, 0);
  }

  buildFlowFrame(set, index) {
    const { palette, valid } = set;
    const { size, radiusInCells, radii, warps } = set.lattice;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    const image = context.createImageData(size, size);
    const pixels = image.data;

    const phase = (index / AIM_FLOW_FRAMES) * Math.PI * 2;

    for (let i = 0; i < radii.length; i++) {
      const radius = radii[i];
      if (radius > radiusInCells) continue;

      /*
       * The gradient. This one term is the entire aim: strongest under the pointer
       * and cooling outward, with no feature anywhere in it that the eye can latch
       * onto as an edge, a ring or a boundary. Where it stops is decided by the
       * body mask it is about to be multiplied into, not by anything here.
       */
      const q = radius / AIM_SPREAD_SCALE;
      const spread = 1 / (1 + q * q);

      /*
       * The surface, drifting. Each patch takes its phase from the noise field, so
       * over one cycle the whole tint stirs without any part of it beating in time
       * with any other — which is what separates weather from a pulse.
       */
      const mottle = 0.5 + 0.5 * Math.cos(warps[i] * AIM_MOTTLE_TURNS + phase);
      const surface = 1 - AIM_MOTTLE_DEPTH + AIM_MOTTLE_DEPTH * mottle;

      /* Eased to nothing over the last stretch so the texture's own edge cannot show. */
      const skirt = Math.min(1, (radiusInCells - radius) / 0.9);

      /*
       * A floor under the gradient, so a wide spray's far cells are still lit. Their
       * job is to say "this is hit too", and a cell at a twentieth strength does not
       * say it — the shape then has to be carried by an outline, which is the thing
       * this renderer refuses.
       */
      /*
       * A refusal is FLATTER as well as colder. The gradient exists to say where the
       * shot is centred, and a shot that will not happen has no centre worth pointing
       * at — so the refused field is nearly even over the ground it would have taken,
       * which is also what keeps its far cells legible without lighting its middle.
       */
      const level = valid ? (0.28 + 0.72 * spread) * surface : (0.6 + 0.4 * spread) * surface;
      /*
       * QUIET. The field is no longer the picture — the stage's preview under it
       * carries the material, and this is the precision laid over it: which ground,
       * exactly. Two layers each stating the same thing at full weight bury the map
       * under a solid slab of colour, and a slab has no boundary to read.
       */
      /*
       * A refusal is not the aim turned down. It is the ONLY layer left — the stage's
       * preview is built from the cells a shot would actually reach, so when there are
       * none it is not there at all, and an aim that only whispers when refused reads
       * as the aim having been lost rather than as the shot being impossible. So the
       * refused state carries MORE alpha than the live one, and states it in a
       * material that is unmistakably not a skill.
       */
      const alpha = level * skirt * (valid ? 0.44 : 0.6);
      if (alpha <= 0.002) continue;

      const offset = i * 4;
      /*
       * A refusal is flat. There is no emitting centre to ramp toward, because a
       * shot that will not land is not lighting anything: only the alpha varies, and
       * the colour stays the one cold slate the whole way out.
       */
      const stop = valid ? rampAt(palette, level) : palette.color;
      pixels[offset] = stop.r;
      pixels[offset + 1] = stop.g;
      pixels[offset + 2] = stop.b;
      pixels[offset + 3] = Math.min(255, alpha * 255);
    }

    context.putImageData(image, 0, 0);
    set.frames[index] = canvas;
  }

  /**
   * Frames are rasterised the first time the cycle asks for them - at most one per
   * draw, so the cost is spread over the first cycle and never batched into a stall.
   * This deliberately does not run off requestAnimationFrame: rAF is suspended
   * outright in a background tab, which left the set stuck on its first frame and the
   * field frozen. Building where the frame is needed cannot get out of step with what
   * is being drawn.
   */
  frameAt(set, index, allowBuild) {
    if (set.frames[index]) return set.frames[index];
    if (allowBuild) {
      this.buildFlowFrame(set, index);
      return set.frames[index];
    }

    const count = set.frames.length;
    for (let offset = 1; offset < count; offset++) {
      const back = set.frames[(index - offset + count) % count];
      if (back) return back;
      const forward = set.frames[(index + offset) % count];
      if (forward) return forward;
    }
    return null;
  }

  /**
   * Composites the impact and lays it on the board through the grid's own basis, so
   * it takes the map's rake and lies in the floor rather than on the glass.
   *
   * The only thing that moves is which pair of flow frames is being blended, and that
   * advances off wall time rather than off a frame counter, so the rings travel at
   * the same slow rate whatever the display refreshes at.
   *
   * @param ctx      the board's own 2D context, already carrying its pan and zoom
   * @param cells    [{ x, y, isPivot? }] in whole grid coordinates
   * @param options  project  (gridX, gridY) => { x, y } — fractional grid to screen,
   *                          the ONE signature both boards adapt to
   *                 color    { r, g, b }
   *                 pivot    the cell the field radiates from, if not marked on cells
   *                 point    fractional aim point, when it tracks the pointer
   *                 valid    false paints the refused state
   *                 locked   a committed charge: full weight, no drift
   */
  draw(ctx, cells, options = {}) {
    if (!ctx || !cells || !cells.length) return;

    const {
      project = null,
      color = { r: 0, g: 224, b: 255 },
      palette = null,
      pivot = null,
      point = null,
      valid = true,
      locked = false
    } = options;

    if (!project) return;

    const source = pivot || cells.find(cell => cell.isPivot) || cells[0];
    const focus = point || { x: source.x + 0.5, y: source.y + 0.5 };

    const material = normalizePalette(palette || color);
    const body = this.bodyTexture(cells, material.color, valid);
    const flow = this.flowFrames(material, valid);

    const width = body.canvas.width;
    const height = body.canvas.height;

    if (!this.composite) this.composite = document.createElement('canvas');
    const composite = this.composite;
    if (composite.width !== width) composite.width = width;
    if (composite.height !== height) composite.height = height;
    const scratch = composite.getContext('2d');

    scratch.setTransform(1, 0, 0, 1, 0, 0);
    scratch.globalCompositeOperation = 'source-over';
    scratch.clearRect(0, 0, width, height);

    const flowSize = flow.radiusInCells * 2;
    const left = (focus.x - flow.radiusInCells - body.x0) * body.pixelsPerCellX;
    const top = (focus.y - flow.radiusInCells - body.y0) * body.pixelsPerCellY;
    const drawWidth = flowSize * body.pixelsPerCellX;
    const drawHeight = flowSize * body.pixelsPerCellY;

    // Two adjacent frames of the cycle, crossed additively at complementary weights.
    // This has to be `lighter`, not `source-over`: stacking the second frame over the
    // first at alpha f gives a result alpha of aB*f + aA*(1 - aB*f), which is a
    // partial union rather than a mix - brighter than either frame, and most so as f
    // approaches one. The field therefore swelled across each frame slot and dropped
    // back when the index advanced, a sawtooth at 12 Hz. Added at (1 - f) and f the
    // alphas sum to exactly the weighted average, and the colours with them, so the
    // rings drift without the brightness moving at all.
    const cycle = (performance.now() / (AIM_FLOW_PERIOD * 1000)) % 1;
    const position = cycle * AIM_FLOW_FRAMES;
    const index = Math.floor(position);
    const blend = position - index;

    const current = this.frameAt(flow, index % AIM_FLOW_FRAMES, true);
    const next = this.frameAt(flow, (index + 1) % AIM_FLOW_FRAMES, false);
    if (!current) return;

    scratch.globalCompositeOperation = 'lighter';
    if (next && next !== current) {
      scratch.globalAlpha = 1 - blend;
      scratch.drawImage(current, left, top, drawWidth, drawHeight);
      scratch.globalAlpha = blend;
      scratch.drawImage(next, left, top, drawWidth, drawHeight);
      scratch.globalAlpha = 1;
    } else {
      scratch.drawImage(current, left, top, drawWidth, drawHeight);
    }

    scratch.globalCompositeOperation = 'destination-in';
    scratch.drawImage(body.canvas, 0, 0);

    const origin = project(body.x0, body.y0);
    const alongX = project(body.x0 + body.spanX, body.y0);
    const alongY = project(body.x0, body.y0 + body.spanY);

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    /*
     * A committed charge is the decision already made, so it is allowed to be heavier
     * than the aim still being moved — but neither may bury the board.
     *
     * The lift on a small claim is the same argument the stage makes with `uAreaNorm`,
     * from the other side. A wide spray states itself by covering ground; a single
     * cell has no ground to cover, so at the weight that keeps a nine-cell blast off
     * the map texture it reads as a smudge under the token. What has to be constant
     * between them is how clearly the aim is THERE, not how much light it puts out.
     */
    const compact = Math.min(1, 4 / Math.max(1, cells.length));
    /*
     * A live aim is one of two layers and a refused one is the only layer there is,
     * so they cannot share a weight. Measured on the board rather than guessed: at the
     * live aim's 0.46 a refusal lifted the rock under it by about eight levels a
     * channel, which is inside what the eye ignores and outside what a screenshot even
     * records. At 0.92 it went the other way and read as fog rolling over the board —
     * louder than the shot it was refusing. 0.56 is the level at which it is plainly
     * there and plainly not an effect.
     */
    const weight = locked ? 0.62 : (valid ? 0.46 : 0.56);
    ctx.globalAlpha = weight * (1 + 0.35 * compact);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.transform(
      (alongX.x - origin.x) / width,
      (alongX.y - origin.y) / width,
      (alongY.x - origin.x) / height,
      (alongY.y - origin.y) / height,
      origin.x,
      origin.y
    );
    ctx.drawImage(composite, 0, 0);
    ctx.restore();
  }

  dispose() {
    this.disposed = true;
    this.bodyCache.clear();
    this.flowCache.clear();
    this.latticeCache = null;
    this.composite = null;
  }
}

export default AimField;
